import React, { useState } from 'react';
import { ButtonCallToAction, ButtonTertiary, PromptConfirmation } from 'ui';
import { StickyHeader, StickyHeaderButtonContainer } from 'app/shell/components/sticky-header/sticky-header';
import { linkCodesToUserGroupId, LinkCodesTranslation } from '../constants';
import { useActionCreators, useDetailsSelectors, useListActionsCallbacks, useListCallbacks, useListSelectors } from '../hooks';
import { ListHeaderActions } from './list-header-actions';
import { ListHeaderVisibility } from './list-header-visibility';
import { ListHeaderSwitch } from 'app/area/link-codes/components/list-header-switch';
import { ActionPayloadType, Visibility } from 'app/area/link-codes/types-store';
import { toastFactory } from 'app/infrastructure/notifications/toast-factory';
import styles from './list-header.scss';

interface ListHeaderProps {
  codetableId: number;
  hasChanges: boolean;
  displayNumberOfResults: boolean;
  numberOfResults: number;
  includeInactive: boolean;
  translation: LinkCodesTranslation;
}

export const ListHeader: React.FC<ListHeaderProps> = ({
  codetableId, hasChanges, includeInactive,
  displayNumberOfResults, numberOfResults, translation
}) => {

  const toasts = toastFactory();
  const { visiblity, activeValue, categoryValue, codetableValue } = useListSelectors();
  const { activeType } = useDetailsSelectors();
  const [shouldPrompt, setShouldPrompt] = useState(false);
  const { updateVisibility } = useListCallbacks();
  const { loadListBranch, clearList } = useActionCreators();

  const showPrompt = () => {
    setShouldPrompt(true);
  };

  const hidePrompt = () => {
    setShouldPrompt(false);
  };

  const { saveChanges, cancelChanges } = useListActionsCallbacks();

  const onSave = () => {
    saveChanges(translation.message_codes_updated, translation.validation_error);
  };

  const showNotification = (nextVisibility: Visibility) => {
    if (visiblity !== nextVisibility) {
      toasts.dispatchInfo(translation.message_limit_visibility);
    }
  };

  const setVisibility = (newVisibility: Visibility) =>
    hasChanges
      ? showNotification(newVisibility)
      : applyVisibility(newVisibility);

  const showAll = () => setVisibility(Visibility.all);
  const showSelected = () => setVisibility(Visibility.selected);
  const showUnSelected = () => setVisibility(Visibility.unselected);

  const applyVisibility = (newVisibility: Visibility) => {
    const visibilityActions = new Map<Visibility, () => void>([
      [Visibility.selected, onShowSelected],
      [Visibility.unselected, onShowUnselected],
      [Visibility.all, onShowAll]
    ]);

    visibilityActions.get(newVisibility)();
  };

  const onShowAll = () => {
    loadList();
    updateVisibility(Visibility.all);
  };

  const onShowSelected = () => {
    loadList(ActionPayloadType.type, false);
    updateVisibility(Visibility.selected);
  };

  const onShowUnselected = () => {
    loadList(ActionPayloadType.type, true);
    updateVisibility(Visibility.unselected);
  };

  const loadList = (type?: string, negated?: boolean) => {
    const model = {
      codetable: codetableValue?.value,
      category: categoryValue?.value,
      activeType: activeType,
      includeInactiveCodes: includeInactive,
      ...(!!type && {
        link: {
          type: codetableId === linkCodesToUserGroupId
            ? ActionPayloadType.userGroup
            : type,
          id: activeValue?.value,
          negated
        }
      })
    };

    clearList(model);
    loadListBranch(model);
  };

  return <>
    {shouldPrompt && <PromptConfirmation
      title={translation.cancel_changes}
      description={translation.are_you_sure}
      continueText={translation.undo_all}
      cancelText={translation.go_back}
      onContinue={cancelChanges}
      onCancel={hidePrompt} />}
    <StickyHeader
      className={styles.header}
      rightSideContent={<StickyHeaderButtonContainer>
        <div className={styles.buttonContainer}>
          <ButtonTertiary
            id="link_codes_cancel_changes"
            onClick={showPrompt}
            disabled={!hasChanges}>
            {translation.cancel_changes}
          </ButtonTertiary>
          <ButtonCallToAction
            id="texts_save_changes"
            onClick={onSave}
            disabled={!hasChanges}>
            {translation.update_codes}
          </ButtonCallToAction>
        </div>
      </StickyHeaderButtonContainer>}>
      <div className={styles.dropdownContainer}>
        <ListHeaderActions
          includeInactive={includeInactive}
          translation={translation} />
        <ListHeaderVisibility
          visiblity={visiblity}
          translation={translation}
          showAll={showAll}
          showSelected={showSelected}
          showUnSelected={showUnSelected} />
      </div>
    </StickyHeader>

    <div className={styles.searchSummary}>
      <div id="link_codes_number_of_results" className={styles.numberOfResults}>
        {displayNumberOfResults && numberOfResults
          ? `${numberOfResults} ${translation.search_hits}`
          : null}
      </div>
      <ListHeaderSwitch
        className={styles.switch}
        hasChanges={hasChanges}
        includeInactive={includeInactive}
        translation={translation} />
    </div>
  </>;
};
